import React, { ChangeEvent, FormEvent, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  AudioLines,
  Camera,
  CheckCircle2,
  ChevronDown,
  Clapperboard,
  Clock3,
  Download,
  ExternalLink,
  FileVideo,
  Layers,
  Link,
  Loader2,
  MessageSquareQuote,
  Play,
  RefreshCw,
  Smartphone,
  Sparkles,
  Tags,
  Type,
  Upload,
  User,
  Users,
} from 'lucide-react';
import { Spinner } from '@heroui/react';
import { Badge, Button, Card, getAuthToken } from '@trafiflow/shared-ui';
import apiClient from '@/shared/lib/api';
import { adGeneratorService } from '@/services/ad-generator';
import type { Brand } from '@/modules/my-company/types';
import type { StoryboardBrief } from '@/modules/marketing/pages/playground/storyboard/storyboards.service';
import {
  extractVideoFrames,
  extractVideoFramesFromUrl,
  geminiSceneToFrame,
  type GeminiScene,
  type StoryboardFrameData,
} from '@/modules/marketing/pages/playground/storyboard/types';
import {
  assembleStoryboard,
  createStoryboard,
  type StoryboardAssemblyResponse,
} from '@/modules/marketing/pages/playground/storyboard/storyboards.service';
import { FootageSelectorModal } from '@/modules/marketing/pages/playground/storyboard/FootageSelectorModal';
import { type CompanyVideoAsset, getCompanyVideo } from '@/modules/marketing/pages/repository/companyVideos.service';
import { useBrandContext } from '@/modules/marketing/hooks/useBrandContext';
import { AdSourceModal } from './AdSourceModal';
import { type FbAd } from '@/services/fb-ads';

// ─── Domain types ────────────────────────────────────────────────────────────

// Persona type from Product Bible
interface Persona {
  name: string;
  age?: string;
  coreProblem?: string;
  emotions?: string[];
  painPoints?: string[];
  angles?: string[];
  strategicImpact?: string;
}

// Product Bible structure
interface ProductBible {
  brandName?: string;
  product?: {
    name?: string;
    valueProposition?: string;
    benefits?: Array<{ name: string; description: string }>;
    proofPoints?: string[];
  };
  personas?: Persona[];
}

interface Scene {
  sceneNumber: number;
  startTime: number;
  endTime: number;
  description: string;
  contentRole: string; // Scene purpose: hook, pain, solution, cta, etc.
  visualType: string;
  audio: string;
  textOverlay: string;
  transitions: string;
  camera: string;
  mood: string;
  keyElements: string[];
  frameUrl?: string; // From backend frame extraction
  _frameDataUrl?: string; // Legacy client-side extraction
}

interface StoryboardMeta {
  durationSec: number;
  scenesCount: number;
  suggestedHooks?: string[];
  brand?: string;
  product?: string;
  model: string;
}

interface StoryboardResult {
  scenes: Scene[];
  meta: StoryboardMeta;
}

interface StoryboardApiResponse {
  jobId: string;
  storyboard: StoryboardResult;
}

interface StoryboardUploadResponse {
  uploadUrl: string;
  s3Key: string;
  videoId: string;
}

interface StoryboardErrorResponse {
  error?: string;
  message?: string;
}

// ─── Storyboard type config ───────────────────────────────────────────────────

type StoryboardTypeId = 'hook-test' | 'creator-swap' | 'ad-structure' | 'full-ad-swipe';

interface StoryboardTypeConfig {
  id: StoryboardTypeId;
  title: string;
  description: string;
  color: string;
  iconColor: string;
  Icon: React.FC<{ className?: string }>;
}

const STORYBOARD_TYPES: StoryboardTypeConfig[] = [
  {
    id: 'hook-test',
    title: 'Hook Test',
    description: 'Create different hook visual options for the same ad',
    color: 'text-blue-600',
    iconColor: 'text-blue-500',
    Icon: ({ className }) => (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a4 4 0 0 1 4 4v4" />
        <path d="M16 10c0 3-1.5 5-4 5s-4-2-4-5" />
        <path d="M8 15c-2 1-3 3-3 5" />
      </svg>
    ),
  },
  {
    id: 'creator-swap',
    title: 'Content Creator Swap',
    description: 'Use a different creator for the same ad',
    color: 'text-pink-500',
    iconColor: 'text-pink-400',
    Icon: ({ className }) => <User className={className} />,
  },
  {
    id: 'ad-structure',
    title: 'Ad Structure Test',
    description: 'Create a new ad structure keeping the original hook',
    color: 'text-teal-600',
    iconColor: 'text-teal-500',
    Icon: ({ className }) => <Layers className={className} />,
  },
  {
    id: 'full-ad-swipe',
    title: 'Full Ad Swipe',
    description: 'Swipe a benchmark ad from any brand',
    color: 'text-orange-500',
    iconColor: 'text-orange-400',
    Icon: ({ className }) => <Smartphone className={className} />,
  },
];

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const STORYBOARD_API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3000').replace(/\/$/, '');

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const wholeSeconds = Math.floor(seconds);
  const mins = Math.floor(wholeSeconds / 60);
  const secs = wholeSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function normalizeStoryboard(result: StoryboardResult): StoryboardResult {
  const scenes = Array.isArray(result?.scenes)
    ? result.scenes.map((scene, index) => ({
      sceneNumber: Number(scene?.sceneNumber ?? index + 1),
      startTime: Number(scene?.startTime ?? 0),
      endTime: Number(scene?.endTime ?? 0),
      description: String(scene?.description ?? ''),
      // contentRole is the scene's purpose (hook, pain, solution, etc.) - essential for video matching
      contentRole: String((scene as Scene & { contentRole?: string })?.contentRole ?? 'scene'),
      visualType: String(scene?.visualType ?? 'unknown'),
      audio: String(scene?.audio ?? ''),
      textOverlay: String(scene?.textOverlay ?? ''),
      transitions: String(scene?.transitions ?? ''),
      camera: String(scene?.camera ?? 'Unknown'),
      mood: String(scene?.mood ?? 'Unknown'),
      keyElements: Array.isArray(scene?.keyElements) ? scene.keyElements.map((item) => String(item)) : [],
      // Preserve frameUrl from backend (server-side frame extraction)
      frameUrl: (scene as Scene & { frameUrl?: string })?.frameUrl || undefined,
    }))
    : [];

  const hooks = Array.isArray(result?.meta?.suggestedHooks)
    ? result.meta.suggestedHooks.map((hook) => String(hook)).filter(Boolean)
    : [];

  return {
    scenes,
    meta: {
      durationSec: Number(result?.meta?.durationSec ?? 0),
      scenesCount: Number(result?.meta?.scenesCount ?? scenes.length),
      suggestedHooks: hooks,
      brand: result?.meta?.brand ? String(result.meta.brand) : undefined,
      product: result?.meta?.product ? String(result.meta.product) : undefined,
      model: String(result?.meta?.model || 'gemini-3-flash'),
    },
  };
}

function safeText(value: string): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : 'Not provided';
}

function humanizeValue(value: string): string {
  return value.split('_').join(' ');
}

function getApiErrorMessage(payload: StoryboardErrorResponse | null | undefined, fallback: string): string {
  return payload?.message || payload?.error || fallback;
}

function uploadVideoToPresignedS3(uploadUrl: string, file: File, onProgress: (value: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
        return;
      }
      reject(new Error(`Failed to upload video (${xhr.status})`));
    };

    xhr.onerror = () => reject(new Error('Failed to upload video to S3'));
    xhr.onabort = () => reject(new Error('Video upload was cancelled'));
    xhr.send(file);
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface TypeCardProps {
  config: StoryboardTypeConfig;
  onClick: () => void;
}

function TypeCard({ config, onClick }: TypeCardProps) {
  return (
    <button
      onClick={onClick}
      className="group flex flex-col items-center gap-3 rounded-xl border border-tf-border bg-white p-8 text-center transition-all duration-150 hover:border-gray-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
    >
      <config.Icon className={`w-9 h-9 ${config.iconColor}`} />
      <span className={`text-base font-semibold ${config.color}`}>{config.title}</span>
      <span className="text-sm text-tf-text-muted leading-snug">{config.description}</span>
    </button>
  );
}


// ─── Generation loading screen ────────────────────────────────────────────────

const ANALYZING_TIPS = [
  'Identifying scene transitions and cuts…',
  'Detecting visual types: talking head, demo, lifestyle…',
  'Extracting text overlays and audio cues…',
  'Mapping funnel stages: Hook, Pain, Solution, CTA…',
  'Generating your scene-by-scene storyboard…',
  'Almost there — finalizing your storyboard…',
];

interface GeneratingScreenProps {
  phase: 'uploading' | 'analyzing';
  uploadProgress: number | null;
  videoName?: string;
}

function GeneratingScreen({ phase, uploadProgress, videoName }: GeneratingScreenProps) {
  const [tipIndex, setTipIndex] = React.useState(0);
  const isUploading = phase === 'uploading';

  React.useEffect(() => {
    if (isUploading) return;
    const id = setInterval(() => {
      setTipIndex(i => (i + 1) % ANALYZING_TIPS.length);
    }, 3200);
    return () => clearInterval(id);
  }, [isUploading]);

  const steps: { label: string; icon: React.ReactNode; done: boolean; active: boolean }[] = [
    {
      label: 'Upload',
      icon: <Upload className="w-5 h-5" />,
      done: !isUploading,
      active: isUploading,
    },
    {
      label: 'AI Analysis',
      icon: <Sparkles className="w-5 h-5" />,
      done: false,
      active: !isUploading,
    },
    {
      label: 'Storyboard',
      icon: <Clapperboard className="w-5 h-5" />,
      done: false,
      active: false,
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] py-16 px-4 text-center">
      {/* Animated orb */}
      <div className="relative mb-10">
        <div className="w-24 h-24 rounded-full bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-200">
          {isUploading
            ? <Upload className="w-10 h-10 text-white" />
            : <Sparkles className="w-10 h-10 text-white animate-pulse" />
          }
        </div>
        {/* Ripple rings */}
        <span className="absolute inset-0 rounded-full border-2 border-blue-400 opacity-40 animate-ping" style={{ animationDuration: '1.8s' }} />
        <span className="absolute -inset-3 rounded-full border border-blue-300 opacity-20 animate-ping" style={{ animationDuration: '2.4s', animationDelay: '0.4s' }} />
      </div>

      {/* Step stepper */}
      <div className="flex items-center gap-0 mb-10">
        {steps.map((step, i) => (
          <React.Fragment key={step.label}>
            <div className="flex flex-col items-center gap-2">
              <div className={[
                'w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500',
                step.done ? 'bg-green-500 text-white shadow-sm' :
                step.active ? 'bg-blue-600 text-white shadow-md shadow-blue-200' :
                'bg-gray-100 text-gray-400',
              ].join(' ')}>
                {step.done ? <CheckCircle2 className="w-5 h-5" /> : step.icon}
              </div>
              <span className={[
                'text-xs font-medium transition-colors duration-300 whitespace-nowrap',
                step.done ? 'text-green-600' :
                step.active ? 'text-blue-600' :
                'text-gray-400',
              ].join(' ')}>{step.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className="w-16 h-0.5 -mt-5 mx-1 rounded-full overflow-hidden bg-gray-200">
                <div className={[
                  'h-full rounded-full transition-all duration-700',
                  step.done ? 'w-full bg-green-400' : 'w-0',
                ].join(' ')} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Title + detail */}
      {isUploading ? (
        <div className="space-y-4 max-w-sm w-full">
          <p className="text-xl font-semibold text-tf-text">Uploading your video</p>
          {videoName && (
            <p className="text-sm text-tf-text-muted truncate px-4">{videoName}</p>
          )}
          <div className="space-y-1.5">
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${uploadProgress ?? 0}%` }}
              />
            </div>
            <p className="text-sm font-medium text-blue-600">{uploadProgress ?? 0}%</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3 max-w-sm">
          <p className="text-xl font-semibold text-tf-text">Analyzing with Gemini AI</p>
          <p
            key={tipIndex}
            className="text-sm text-tf-text-muted leading-relaxed animate-pulse min-h-[40px] flex items-center justify-center"
          >
            {ANALYZING_TIPS[tipIndex]}
          </p>
          <p className="text-xs text-tf-text-muted">Usually takes 30–60 seconds</p>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

type Step = 'select-type' | 'upload' | 'result';

export function StoryboardGenerator() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const { buildPrompt } = useBrandContext();

  const [step, setStep] = useState<Step>('select-type');
  const [selectedType, setSelectedType] = useState<StoryboardTypeConfig | null>(null);
  const [showSourceModal, setShowSourceModal] = useState(false);

  const [video, setVideo] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [selectedFbAd, setSelectedFbAd] = useState<FbAd | null>(null);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [loadingPhase, setLoadingPhase] = useState<'uploading' | 'analyzing' | null>(null);
  const [result, setResult] = useState<StoryboardResult | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Footage replacement state
  const [footageModalScene, setFootageModalScene] = useState<Scene | null>(null);
  const [replacedFootage, setReplacedFootage] = useState<Map<number, { video: CompanyVideoAsset; videoUrl: string }>>(new Map());

  // Video assembly state
  const [assemblyStatus, setAssemblyStatus] = useState<'idle' | 'assembling' | 'completed' | 'failed'>('idle');
  const [assemblyResult, setAssemblyResult] = useState<StoryboardAssemblyResponse | null>(null);
  const [assemblyError, setAssemblyError] = useState<string | null>(null);

  // Brand/Persona state for Product Bible context
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [productBible, setProductBible] = useState<ProductBible | null>(null);
  const [loadingPersonas, setLoadingPersonas] = useState(false);
  const [selectedPersonaName, setSelectedPersonaName] = useState<string | null>(null);
  const [showBrandContext, setShowBrandContext] = useState(false);

  // Fetch brands when brand context is shown
  useEffect(() => {
    if (!showBrandContext || brands.length > 0) return;

    async function fetchBrands() {
      setLoadingBrands(true);
      try {
        const response = await apiClient.get('/brands');
        setBrands(response.data.brands || []);
      } catch (err) {
        console.warn('[StoryboardGenerator] Failed to load brands:', err);
        setBrands([]);
      } finally {
        setLoadingBrands(false);
      }
    }

    fetchBrands();
  }, [showBrandContext, brands.length]);

  // Fetch Product Bible when brand is selected
  const fetchProductBible = useCallback(async (brandUrl: string) => {
    setLoadingPersonas(true);
    setPersonas([]);
    setProductBible(null);
    setSelectedPersonaName(null);

    try {
      // Find completed ad-generator jobs for this brand
      const jobsResponse = await adGeneratorService.listJobs();
      const completedJob = jobsResponse.jobs.find(
        (j) => j.status === 'completed' && j.url === brandUrl
      );

      if (completedJob) {
        // Fetch product_bible artifact
        const stageDetail = await adGeneratorService.getStageDetail(
          completedJob.job_id,
          'product_bible'
        );

        if (stageDetail.artifact) {
          const bible = stageDetail.artifact as ProductBible;
          setProductBible(bible);
          setPersonas(bible.personas || []);
        }
      }
    } catch (err) {
      console.warn('[StoryboardGenerator] Failed to load product bible:', err);
    } finally {
      setLoadingPersonas(false);
    }
  }, []);

  // Handle brand selection
  const handleBrandChange = useCallback((brandId: string) => {
    setSelectedBrandId(brandId);
    setSelectedPersonaName(null);

    const selectedBrand = brands.find((b) => b.id === brandId);
    if (selectedBrand?.url) {
      fetchProductBible(selectedBrand.url);
    }
  }, [brands, fetchProductBible]);

  // Build brief from selected brand/persona
  const buildBrief = useCallback((): StoryboardBrief | undefined => {
    if (!selectedBrandId && !selectedPersonaName) return undefined;

    const selectedBrand = brands.find((b) => b.id === selectedBrandId);
    const selectedPersona = personas.find((p) => p.name === selectedPersonaName);

    return {
      brandName: selectedBrand?.name || productBible?.brandName,
      valueProposition: productBible?.product?.valueProposition,
      productBenefits: productBible?.product?.benefits?.map((b) => b.name),
      proofPoints: productBible?.product?.proofPoints,
      personaName: selectedPersona?.name,
      personaAge: selectedPersona?.age,
      personaProblem: selectedPersona?.coreProblem,
      personaEmotions: selectedPersona?.emotions,
      personaAngles: selectedPersona?.angles,
    };
  }, [selectedBrandId, selectedPersonaName, brands, personas, productBible]);

  const sortedScenes = useMemo(() => {
    if (!result) return [] as Scene[];
    return [...result.scenes].sort((a, b) => a.sceneNumber - b.sceneNumber);
  }, [result]);

  const handleReplaceClick = (scene: Scene) => {
    setFootageModalScene(scene);
  };

  const handleFootageSelect = async (video: CompanyVideoAsset) => {
    if (!footageModalScene) return;

    try {
      // Fetch the video details to get presigned URL
      const videoDetails = await getCompanyVideo(video.id);
      const videoUrl = videoDetails.cleanVersionUrl || videoDetails.downloadUrl || '';

      if (videoUrl) {
        setReplacedFootage((prev) => {
          const newMap = new Map(prev);
          newMap.set(footageModalScene.sceneNumber, { video, videoUrl });
          return newMap;
        });
      }
    } catch (err) {
      console.error('[StoryboardGenerator] Failed to get video URL:', err);
    }

    setFootageModalScene(null);
  };

  const handleAssembleVideo = async () => {
    if (!result || assemblyStatus === 'assembling') return;

    setAssemblyStatus('assembling');
    setAssemblyError(null);

    try {
      // Build scenes array from sorted scenes with any replaced footage
      const scenes = sortedScenes.map((scene) => {
        const replaced = replacedFootage.get(scene.sceneNumber);
        return {
          sceneNumber: scene.sceneNumber,
          startTime: scene.startTime,
          endTime: scene.endTime,
          description: scene.description,
          contentRole: scene.contentRole, // Scene purpose for video matching (hook, pain, solution, etc.)
          visualType: scene.visualType,
          audio: scene.audio,
          textOverlay: scene.textOverlay,
          mood: scene.mood,
          keyElements: scene.keyElements,
          // Include replaced video info if available
          ...(replaced?.video?.id && { videoId: replaced.video.id }),
        };
      });

      const response = await assembleStoryboard({
        storyboard: {
          scenes,
          meta: {
            durationSec: result.meta.durationSec,
            scenesCount: result.meta.scenesCount,
            brand: result.meta.brand,
            product: result.meta.product,
          },
        },
        outputFormat: 'mp4',
        aspectRatio: '9:16',
        includeAudio: true,
      });

      if (response.success) {
        setAssemblyResult(response);
        setAssemblyStatus('completed');
      } else {
        setAssemblyError(response.errorMessage || 'Assembly failed');
        setAssemblyStatus('failed');
      }
    } catch (err) {
      console.error('[StoryboardGenerator] Assembly failed:', err);
      setAssemblyError(err instanceof Error ? err.message : 'Failed to assemble video');
      setAssemblyStatus('failed');
    }
  };

  const handleDownloadVideo = () => {
    if (!assemblyResult?.downloadUrl) return;
    window.open(assemblyResult.downloadUrl, '_blank');
  };

  // ── Handlers ──

  const handleTypeSelect = (config: StoryboardTypeConfig) => {
    setSelectedType(config);
    setShowSourceModal(true);
  };

  const handleSourceUpload = (file: File) => {
    setShowSourceModal(false);
    setVideoFile(file);
    setVideoUrl(null);
    setSelectedFbAd(null);
    setStep('upload');
  };

  const handleSourcePasteUrl = (url: string) => {
    setShowSourceModal(false);
    setVideoUrl(url);
    setVideo(null);
    setSelectedFbAd(null);
    setStep('upload');
  };

  const handleSourceSelectAd = (ad: FbAd, adVideoUrl: string) => {
    setShowSourceModal(false);
    setSelectedFbAd(ad);
    setVideoUrl(adVideoUrl);
    setVideo(null);
    setStep('upload');
  };

  const handleCloseModal = () => {
    setShowSourceModal(false);
    setSelectedType(null);
  };

  const setVideoFile = (file: File | null) => {
    if (!file) {
      setVideo(null);
      return;
    }
    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      setError('Invalid file type. Use MP4, MOV, or WebM.');
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      setError('Video file exceeds the 100MB limit.');
      return;
    }
    setError(null);
    setVideo(file);
  };

  const handleVideoChange = (event: ChangeEvent<HTMLInputElement>) => {
    setVideoFile(event.target.files?.[0] ?? null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if ((!video && !videoUrl) || loading) return;

    setLoading(true);
    setError(null);

    try {
      const authToken = await getAuthToken();
      const authHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      if (authToken) authHeaders['Authorization'] = `Bearer ${authToken}`;

      let s3Key: string | null = null;
      let mimeType = 'video/mp4';

      // If we have a file, upload it first
      if (video) {
        setUploadProgress(0);
        setLoadingPhase('uploading');

        const uploadResponse = await fetch(`${STORYBOARD_API_URL}/api/storyboard/upload`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ filename: video.name, contentType: video.type }),
        });

        const uploadPayload = (await uploadResponse.json()) as Partial<StoryboardUploadResponse> & StoryboardErrorResponse;
        if (!uploadResponse.ok) throw new Error(getApiErrorMessage(uploadPayload, 'Failed to initialize upload'));
        if (!uploadPayload.uploadUrl || !uploadPayload.s3Key)
          throw new Error('Upload endpoint did not return uploadUrl and s3Key');

        await uploadVideoToPresignedS3(uploadPayload.uploadUrl, video, (p) => setUploadProgress(p));
        s3Key = uploadPayload.s3Key;
        mimeType = video.type;
      }

      setUploadProgress(null);
      setLoadingPhase('analyzing');

      // Generate storyboard - POST returns 202 + jobId immediately, then poll for result
      const generateBody: Record<string, unknown> = {
        customPrompt: buildPrompt(prompt.trim() || undefined),
        storyboardType: selectedType?.id,
      };

      // Include brief with brand/persona context if selected
      const brief = buildBrief();
      if (brief) {
        generateBody.brief = brief;
      }

      if (s3Key) {
        generateBody.s3Key = s3Key;
        generateBody.mimeType = mimeType;
      } else if (videoUrl) {
        generateBody.videoUrl = videoUrl;
        generateBody.mimeType = 'video/mp4';
      }

      const generateResponse = await fetch(`${STORYBOARD_API_URL}/api/storyboard/generate`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(generateBody),
      });

      const queuePayload = (await generateResponse.json()) as { jobId?: string; status?: string } & StoryboardErrorResponse;
      if (!generateResponse.ok) throw new Error(getApiErrorMessage(queuePayload, 'Failed to queue storyboard generation'));
      const { jobId: generationJobId } = queuePayload;
      if (!generationJobId) throw new Error('Server did not return a jobId');

      // Poll until completed or failed (max 5 min: 60 × 5s)
      const POLL_INTERVAL_MS = 5000;
      const MAX_POLLS = 60;
      type GenerateStatusResponse = Partial<StoryboardApiResponse> & StoryboardErrorResponse & { status?: string; errorMessage?: string };

      let payload: GenerateStatusResponse | null = null;

      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
        const statusRes = await fetch(
          `${STORYBOARD_API_URL}/api/storyboard/generate/status/${generationJobId}`,
          { headers: authHeaders }
        );
        const statusPayload = (await statusRes.json()) as GenerateStatusResponse;
        if (!statusRes.ok) throw new Error(getApiErrorMessage(statusPayload, 'Failed to poll generation status'));

        if (statusPayload.status === 'completed') {
          payload = statusPayload;
          break;
        }
        if (statusPayload.status === 'failed') {
          throw new Error(statusPayload.errorMessage || 'Storyboard generation failed');
        }
        // still pending/running — keep polling
      }

      if (!payload) throw new Error('Storyboard generation timed out. Please try again.');
      if (!payload.storyboard) throw new Error('API did not return a storyboard payload');

      const normalized = normalizeStoryboard(payload.storyboard);

      // Check if backend provided frame URLs (server-side extraction)
      const hasBackendFrames = normalized.scenes.some((scene) => scene.frameUrl);

      if (hasBackendFrames) {
        // Use backend frame URLs - copy frameUrl to _frameDataUrl for compatibility
        console.log('[StoryboardGenerator] Using backend-extracted frames');
        normalized.scenes = normalized.scenes.map((scene) => ({
          ...scene,
          _frameDataUrl: scene.frameUrl || '',
        }));
      } else {
        // Fallback: try client-side frame extraction
        console.log('[StoryboardGenerator] No backend frames, attempting client-side extraction');
        try {
          let frameMap: Map<number, string> | null = null;

          if (video) {
            // Extract frames from uploaded file
            frameMap = await extractVideoFrames(video, normalized.scenes as GeminiScene[]);
          } else if (videoUrl) {
            // Extract frames from URL-based video (may fail due to CORS)
            frameMap = await extractVideoFramesFromUrl(videoUrl, normalized.scenes as GeminiScene[]);
          }

          if (frameMap && frameMap.size > 0) {
            normalized.scenes = normalized.scenes.map((scene, i) => ({
              ...scene,
              _frameDataUrl: frameMap!.get(i) ?? '',
            }));
          } else if (selectedFbAd) {
            // Last resort: use ad thumbnail as fallback
            const adImageUrl = selectedFbAd.media_urls?.find(
              (url) => url && !url.toLowerCase().includes('.mp4') && !url.toLowerCase().includes('.webm') && !url.toLowerCase().includes('.mov')
            );
            if (adImageUrl) {
              console.log('[StoryboardGenerator] Using ad thumbnail as fallback:', adImageUrl);
              normalized.scenes = normalized.scenes.map((scene, i) => ({
                ...scene,
                _frameDataUrl: i === 0 ? adImageUrl : '',
              }));
            }
          }
        } catch (frameErr) {
          console.warn('Frame extraction failed, continuing without thumbnails:', frameErr);
        }
      }

      setJobId(generationJobId);
      setResult(normalized);
      setStep('result');
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Failed to generate storyboard');
    } finally {
      setUploadProgress(null);
      setLoadingPhase(null);
      setLoading(false);
    }
  };

  const reset = () => {
    setVideo(null);
    setVideoUrl(null);
    setSelectedFbAd(null);
    setPrompt('');
    setResult(null);
    setJobId(null);
    setError(null);
    setUploadProgress(null);
    setLoadingPhase(null);
    setSelectedType(null);
    setStep('select-type');
    setReplacedFootage(new Map());
    setAssemblyStatus('idle');
    setAssemblyResult(null);
    setAssemblyError(null);
    // Reset brand/persona state
    setSelectedBrandId(null);
    setSelectedPersonaName(null);
    setPersonas([]);
    setProductBible(null);
    setShowBrandContext(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const openInStoryboardEditor = async () => {
    if (!result) return;

    // Source frames: keep original text from the reference video
    // Include videoUrl for playback when frame extraction failed (CORS)
    const sourceFrames: StoryboardFrameData[] = sortedScenes.map((scene, index) => {
      const frame = geminiSceneToFrame(scene as GeminiScene, index);
      if (scene._frameDataUrl) frame.image = scene._frameDataUrl;
      // Store video URL for playback - allows "Show Video" to work even without thumbnails
      if (videoUrl && !frame.image) {
        frame.videoUrl = videoUrl;
      }
      return frame;
    });

    // Benchmark frames: pre-populate with source text for reference
    // AI adaptation will modify these later if brand context is available
    const benchmarkFrames: StoryboardFrameData[] = sortedScenes.map((scene, index) => {
      const frame = geminiSceneToFrame(scene as GeminiScene, index);
      // Keep the text overlay and audio from source - AI will adapt if brand context is set
      // Keep image empty - auto-fill will assign footage from repository
      frame.image = '';
      return frame;
    });

    let storyboardName = 'New Storyboard';
    if (result.meta.product) storyboardName = `${result.meta.product} Storyboard`;
    else if (result.meta.brand) storyboardName = `${result.meta.brand} Storyboard`;

    setIsSaving(true);
    try {
      const savedStoryboard = await createStoryboard({
        name: storyboardName,
        sourceFrames,
        frames: benchmarkFrames,
        geminiJobId: jobId || undefined,
      });
      navigate(`/marketing/playground/storyboard/${savedStoryboard.id}`, {
        state: { sourceFrames, generatedFrames: benchmarkFrames, meta: result.meta, jobId },
      });
    } catch (saveError) {
      console.error('Failed to save storyboard:', saveError);
      navigate('/marketing/playground/storyboard/new', {
        state: { sourceFrames, generatedFrames: benchmarkFrames, meta: result.meta, jobId },
      });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ──

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_VIDEO_TYPES.join(',')}
        className="hidden"
        onChange={handleVideoChange}
      />

      {showSourceModal && selectedType && (
        <AdSourceModal
          isOpen={showSourceModal}
          storyboardType={selectedType.title}
          onClose={handleCloseModal}
          onSelectAd={handleSourceSelectAd}
          onUpload={handleSourceUpload}
          onPasteUrl={handleSourcePasteUrl}
        />
      )}

      {footageModalScene && (
        <FootageSelectorModal
          scene={footageModalScene as GeminiScene}
          onSelect={handleFootageSelect}
          onClose={() => setFootageModalScene(null)}
        />
      )}

      <div className="p-6 max-w-7xl mx-auto space-y-8">
        {error && (
          <Card className="border border-red-200 bg-tf-feedback-error-bg">
            <div className="p-4 flex flex-wrap items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-sm text-red-700 flex-1">{error}</p>
              <Button variant="plain" size="sm" onClick={() => setError(null)}>
                Dismiss
              </Button>
            </div>
          </Card>
        )}

        {/* ── Step 1: Type selection ── */}
        {step === 'select-type' && (
          <div className="space-y-8">
            <header className="text-center space-y-3">
              <h1 className="text-3xl font-bold text-tf-text">Storyboard Creator</h1>
              <p className="text-tf-text-muted max-w-lg mx-auto">
                Choose the type of storyboard test you want to run. You can experiment with different visuals for a
                hook, swap creators, or test a completely new ad structure.
              </p>
              <p className="text-tf-text-muted text-sm">
                Note: Our storyboards currently focus on visual and structural changes, not copy or transcript
                suggestions.
              </p>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto w-full">
              {STORYBOARD_TYPES.map((config) => (
                <TypeCard key={config.id} config={config} onClick={() => handleTypeSelect(config)} />
              ))}
            </div>
          </div>
        )}

        {/* ── Step 2: Upload ── */}
        {step === 'upload' && (
          <>
            {/* Full-screen generation loader — hides the form while processing */}
            {loading && loadingPhase ? (
              <GeneratingScreen
                phase={loadingPhase}
                uploadProgress={uploadProgress}
                videoName={video?.name}
              />
            ) : (
              <>
                <header className="flex items-center gap-3">
                  <button onClick={reset} className="text-sm text-tf-text-muted hover:text-tf-text transition-colors">
                    ← Back
                  </button>
                  {selectedType && (
                    <span className={`text-sm font-semibold ${selectedType.color}`}>{selectedType.title}</span>
                  )}
                </header>

                <Card>
                  <div className="p-6 md:p-8 space-y-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className="text-lg font-semibold text-tf-text">
                          {selectedFbAd ? 'Selected Ad' : videoUrl ? 'Video URL' : 'Upload Video'}
                        </h2>
                        <p className="text-sm text-tf-text-muted mt-1">
                          {selectedFbAd
                            ? `Using ad from ${selectedFbAd.page_name}`
                            : videoUrl
                            ? 'Using video from URL'
                            : 'Supported formats: MP4, MOV, WebM. Maximum file size: 100MB.'}
                        </p>
                      </div>
                      <Badge variant="info">Gemini 3 Flash</Badge>
                    </div>

                    <form className="space-y-5" onSubmit={handleSubmit}>
                      <div className="rounded-xl border border-dashed border-tf-border bg-tf-bg-muted p-6">
                        {/* Selected Ad from Repository */}
                        {selectedFbAd && videoUrl ? (
                          <div className="space-y-4">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                                  <Play className="w-5 h-5" />
                                </div>
                                <div>
                                  <p className="font-medium text-tf-text">{selectedFbAd.page_name}</p>
                                  <p className="text-sm text-tf-text-muted">Selected from Ads Repository</p>
                                </div>
                              </div>
                              <Button type="button" variant="secondary" onClick={() => setShowSourceModal(true)}>
                                Change Source
                              </Button>
                            </div>
                            <div className="rounded-lg overflow-hidden border border-tf-border bg-black aspect-video max-w-md mx-auto">
                              <video
                                src={videoUrl}
                                controls
                                className="w-full h-full object-contain"
                              />
                            </div>
                          </div>
                        ) : videoUrl && !selectedFbAd ? (
                          /* Video URL pasted */
                          <div className="space-y-4">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-green-100 text-green-700 flex items-center justify-center">
                                  <Link className="w-5 h-5" />
                                </div>
                                <div>
                                  <p className="font-medium text-tf-text">Video URL</p>
                                  <p className="text-sm text-tf-text-muted truncate max-w-xs">{videoUrl}</p>
                                </div>
                              </div>
                              <Button type="button" variant="secondary" onClick={() => setShowSourceModal(true)}>
                                Change Source
                              </Button>
                            </div>
                            <div className="rounded-lg overflow-hidden border border-tf-border bg-black aspect-video max-w-md mx-auto">
                              <video
                                src={videoUrl}
                                controls
                                className="w-full h-full object-contain"
                              />
                            </div>
                          </div>
                        ) : video ? (
                          /* Uploaded file */
                          <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
                                <FileVideo className="w-5 h-5" />
                              </div>
                              <div>
                                <p className="font-medium text-tf-text">{video.name}</p>
                                <p className="text-sm text-tf-text-muted">{formatBytes(video.size)}</p>
                              </div>
                            </div>
                            <Button type="button" variant="secondary" onClick={() => setShowSourceModal(true)}>
                              Change Source
                            </Button>
                          </div>
                        ) : (
                          /* Empty state */
                          <div className="text-center space-y-3">
                            <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
                              <Clapperboard className="w-6 h-6" />
                            </div>
                            <p className="text-sm text-tf-text-sub">No video selected yet.</p>
                            <Button type="button" variant="primary" onClick={() => setShowSourceModal(true)}>
                              Select Video
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Brand Context Section */}
                      <div className="border border-tf-border rounded-xl overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setShowBrandContext(!showBrandContext)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-tf-bg-muted hover:bg-tf-bg-hover transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-tf-text-muted" />
                            <span className="text-sm font-medium text-tf-text">
                              Brand & Persona Context
                            </span>
                            {selectedBrandId && (
                              <Badge variant="info" className="text-xs">
                                {brands.find((b) => b.id === selectedBrandId)?.name}
                              </Badge>
                            )}
                            {selectedPersonaName && (
                              <Badge variant="success" className="text-xs">
                                {selectedPersonaName}
                              </Badge>
                            )}
                          </div>
                          <ChevronDown
                            className={`w-4 h-4 text-tf-text-muted transition-transform ${
                              showBrandContext ? 'rotate-180' : ''
                            }`}
                          />
                        </button>

                        {showBrandContext && (
                          <div className="p-4 space-y-4 border-t border-tf-border">
                            <p className="text-sm text-tf-text-muted">
                              Select a brand and buyer persona from the Product Bible to help the AI generate more targeted storyboard analysis.
                            </p>

                            {/* Brand Selector */}
                            <div className="space-y-1.5">
                              <label className="text-sm font-medium text-tf-text-sub">Brand</label>
                              <div className="relative">
                                <select
                                  value={selectedBrandId || ''}
                                  onChange={(e) => handleBrandChange(e.target.value)}
                                  disabled={loadingBrands}
                                  className="w-full h-10 rounded-lg border border-tf-border bg-tf-bg-card px-3 text-sm appearance-none cursor-pointer disabled:opacity-50"
                                >
                                  <option value="">
                                    {loadingBrands ? 'Loading brands...' : 'Select a brand (optional)'}
                                  </option>
                                  {brands.map((brand) => (
                                    <option key={brand.id} value={brand.id}>
                                      {brand.name}
                                    </option>
                                  ))}
                                </select>
                                {loadingBrands ? (
                                  <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-tf-text-muted" />
                                ) : (
                                  <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-tf-text-muted pointer-events-none" />
                                )}
                              </div>
                            </div>

                            {/* Persona Selector */}
                            {selectedBrandId && (
                              <div className="space-y-1.5">
                                <label className="text-sm font-medium text-tf-text-sub">Buyer Persona</label>
                                <div className="relative">
                                  <select
                                    value={selectedPersonaName || ''}
                                    onChange={(e) => setSelectedPersonaName(e.target.value || null)}
                                    disabled={loadingPersonas}
                                    className="w-full h-10 rounded-lg border border-tf-border bg-tf-bg-card px-3 text-sm appearance-none cursor-pointer disabled:opacity-50"
                                  >
                                    <option value="">
                                      {loadingPersonas
                                        ? 'Loading personas...'
                                        : personas.length === 0
                                        ? 'No personas available - run Ad Generator first'
                                        : 'Select a persona (optional)'}
                                    </option>
                                    {personas.map((persona) => (
                                      <option key={persona.name} value={persona.name}>
                                        {persona.name}
                                        {persona.age ? ` (${persona.age})` : ''}
                                      </option>
                                    ))}
                                  </select>
                                  {loadingPersonas ? (
                                    <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-tf-text-muted" />
                                  ) : (
                                    <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-tf-text-muted pointer-events-none" />
                                  )}
                                </div>

                                {/* Persona Preview */}
                                {selectedPersonaName && (
                                  <div className="mt-2 p-3 bg-tf-bg-muted rounded-lg border border-tf-border">
                                    {(() => {
                                      const persona = personas.find((p) => p.name === selectedPersonaName);
                                      return (
                                        <>
                                          {persona?.coreProblem && (
                                            <p className="text-sm text-tf-text-muted">
                                              <span className="font-medium text-tf-text">Core Problem:</span>{' '}
                                              {persona.coreProblem}
                                            </p>
                                          )}
                                          {persona?.angles && persona.angles.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-1">
                                              {persona.angles.slice(0, 3).map((angle, i) => (
                                                <span
                                                  key={i}
                                                  className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded"
                                                >
                                                  {angle}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <label className="text-sm font-medium text-tf-text-sub">
                          Custom Instructions (optional)
                        </label>
                        <textarea
                          rows={4}
                          placeholder="Example: Focus on premium product framing, emphasize before/after moments, and recommend punchy social hooks."
                          value={prompt}
                          onChange={(e) => setPrompt(e.target.value)}
                          className="w-full rounded-xl border border-tf-border bg-tf-bg-card px-3 py-2.5 text-sm text-tf-text placeholder:text-tf-text-muted resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={(!video && !videoUrl) || loading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Sparkles className="w-4 h-4" />
                        Generate Storyboard
                      </button>
                    </form>
                  </div>
                </Card>
              </>
            )}
          </>
        )}

        {/* ── Step 3: Results ── */}
        {step === 'result' && result && (
          <section className="space-y-5">
            <Card>
              <div className="p-6 flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-semibold">Storyboard Generated</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="default">{result.meta.scenesCount} scenes</Badge>
                    <Badge variant="default">{formatTime(result.meta.durationSec)} duration</Badge>
                    <Badge variant="info">Model: {result.meta.model}</Badge>
                    {jobId && <Badge variant="warning">Job: {jobId}</Badge>}
                  </div>
                  {(result.meta.brand || result.meta.product) && (
                    <p className="text-sm text-tf-text-sub">
                      {result.meta.brand ? `Brand: ${result.meta.brand}` : ''}
                      {result.meta.brand && result.meta.product ? ' · ' : ''}
                      {result.meta.product ? `Product: ${result.meta.product}` : ''}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="primary"
                    onClick={openInStoryboardEditor}
                    disabled={isSaving}
                    icon={isSaving ? <Spinner color="current" size="sm" /> : <ExternalLink className="w-4 h-4" />}
                  >
                    {isSaving ? 'Saving...' : 'Open in Storyboard Editor'}
                  </Button>
                  <Button variant="secondary" onClick={reset}>
                    Generate Another
                  </Button>
                </div>
              </div>
            </Card>

            {result.meta.suggestedHooks && result.meta.suggestedHooks.length > 0 && (
              <Card>
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <MessageSquareQuote className="w-5 h-5 text-blue-700" />
                    <h2 className="text-lg font-semibold text-tf-text">Suggested Hooks</h2>
                  </div>
                  <div className="grid gap-3 md:grid-cols-3">
                    {result.meta.suggestedHooks.slice(0, 3).map((hook, index) => (
                      <div key={`${hook}-${index}`} className="rounded-lg border border-blue-100 bg-tf-feedback-info-bg p-4">
                        <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Hook {index + 1}</p>
                        <p className="text-sm text-tf-text mt-1">{hook}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}

            {/* Video Assembly Section */}
            <Card>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Play className="w-5 h-5 text-violet-600" />
                    <h2 className="text-lg font-semibold text-tf-text">Video Assembly</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    {assemblyStatus === 'idle' && (
                      <Button
                        variant="primary"
                        onClick={handleAssembleVideo}
                        icon={<Play className="w-4 h-4" />}
                      >
                        Assemble Video
                      </Button>
                    )}
                    {assemblyStatus === 'assembling' && (
                      <Button variant="primary" disabled icon={<Spinner color="current" size="sm" />}>
                        Assembling...
                      </Button>
                    )}
                    {assemblyStatus === 'completed' && assemblyResult?.downloadUrl && (
                      <Button
                        variant="primary"
                        onClick={handleDownloadVideo}
                        icon={<Download className="w-4 h-4" />}
                      >
                        Download Video
                      </Button>
                    )}
                    {assemblyStatus === 'failed' && (
                      <Button
                        variant="secondary"
                        onClick={handleAssembleVideo}
                        icon={<RefreshCw className="w-4 h-4" />}
                      >
                        Retry Assembly
                      </Button>
                    )}
                  </div>
                </div>

                {assemblyStatus === 'idle' && (
                  <p className="text-sm text-tf-text-muted">
                    Click "Assemble Video" to match your storyboard scenes to company footage and create a downloadable video.
                    {replacedFootage.size > 0 && (
                      <span className="text-violet-600 font-medium"> ({replacedFootage.size} scene(s) with custom footage)</span>
                    )}
                  </p>
                )}

                {assemblyStatus === 'assembling' && (
                  <div className="space-y-3">
                    <p className="text-sm text-tf-text-muted">
                      Matching scenes to footage and assembling video. This may take a moment...
                    </p>
                    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full bg-violet-500 rounded-full animate-pulse w-2/3" />
                    </div>
                  </div>
                )}

                {assemblyStatus === 'completed' && assemblyResult && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Video assembled successfully!</span>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {assemblyResult.durationSec && (
                        <Badge variant="default">{formatTime(assemblyResult.durationSec)} duration</Badge>
                      )}
                      <Badge variant="info">
                        {assemblyResult.sceneMatches?.filter(m => m.matched).length || 0} / {assemblyResult.sceneMatches?.length || 0} scenes matched
                      </Badge>
                    </div>

                    {assemblyResult.videoUrl && (
                      <div className="rounded-lg overflow-hidden border border-tf-border bg-black">
                        <video
                          src={assemblyResult.videoUrl}
                          controls
                          className="w-full max-h-[400px]"
                          poster=""
                        />
                      </div>
                    )}

                    {assemblyResult.sceneMatches && assemblyResult.sceneMatches.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide">Scene Matches</p>
                        <div className="grid gap-2 md:grid-cols-2">
                          {assemblyResult.sceneMatches.map((match) => (
                            <div
                              key={match.sceneNumber}
                              className={`rounded-lg border p-3 ${
                                match.matched
                                  ? 'border-green-200 bg-green-50'
                                  : 'border-orange-200 bg-orange-50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">
                                  Scene {match.sceneNumber}: {match.contentRole}
                                </span>
                                <Badge variant={match.matched ? 'success' : 'warning'}>
                                  {match.matched ? `${Math.round(match.matchScore * 100)}% match` : 'No match'}
                                </Badge>
                              </div>
                              {match.videoTitle && (
                                <p className="text-xs text-tf-text-muted mt-1 truncate">
                                  {match.videoTitle}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {assemblyStatus === 'failed' && assemblyError && (
                  <div className="flex items-start gap-2 text-red-600">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">{assemblyError}</p>
                  </div>
                )}
              </div>
            </Card>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-tf-text-sub" />
                <h2 className="text-lg font-semibold text-tf-text">Scene Breakdown</h2>
              </div>

              {sortedScenes.map((scene) => {
                const replacedVideo = replacedFootage.get(scene.sceneNumber);
                return (
                  <Card key={`scene-${scene.sceneNumber}-${scene.startTime}`}>
                    <div className="p-5 space-y-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700">
                            {scene.sceneNumber}
                          </span>
                          <p className="text-base font-semibold text-tf-text">Scene {scene.sceneNumber}</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="default">{humanizeValue(scene.visualType)}</Badge>
                          <Badge variant="default">{scene.mood}</Badge>
                          <Badge variant="default">{humanizeValue(scene.transitions) || 'cut_to'}</Badge>
                          <button
                            type="button"
                            onClick={() => handleReplaceClick(scene)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors"
                            title="Replace footage"
                          >
                            <RefreshCw className="w-3 h-3" />
                            Replace
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-tf-text-muted">
                        <span className="inline-flex items-center gap-1">
                          <Clock3 className="w-4 h-4" />
                          {formatTime(scene.startTime)} - {formatTime(scene.endTime)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Camera className="w-4 h-4" />
                          {scene.camera}
                        </span>
                      </div>

                      <div className="flex gap-4">
                        {replacedVideo ? (
                          <div className="w-24 flex-shrink-0 relative group">
                            <video
                              src={replacedVideo.videoUrl}
                              className="w-full h-auto rounded-lg border border-violet-300 object-cover"
                              muted
                              autoPlay
                              loop
                              playsInline
                            />
                            <div className="absolute inset-0 bg-violet-600/10 rounded-lg flex items-center justify-center">
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-600 text-white">
                                Replaced
                              </span>
                            </div>
                          </div>
                        ) : scene._frameDataUrl ? (
                          <img
                            src={scene._frameDataUrl}
                            alt={`Scene ${scene.sceneNumber} frame`}
                            className="w-24 h-auto rounded-lg border border-tf-border object-cover flex-shrink-0"
                          />
                        ) : null}
                        <p className="text-tf-text leading-relaxed flex-1">{safeText(scene.description)}</p>
                      </div>

                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="rounded-lg border border-tf-border p-3 bg-tf-bg-muted">
                          <p className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide inline-flex items-center gap-1">
                            <AudioLines className="w-3.5 h-3.5" /> Audio / Voiceover
                          </p>
                          <p className="text-sm text-tf-text mt-2">{safeText(scene.audio)}</p>
                        </div>
                        <div className="rounded-lg border border-tf-border p-3 bg-tf-bg-muted">
                          <p className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide inline-flex items-center gap-1">
                            <Type className="w-3.5 h-3.5" /> Text Overlay
                          </p>
                          <p className="text-sm text-tf-text mt-2">{safeText(scene.textOverlay)}</p>
                        </div>
                        <div className="rounded-lg border border-tf-border p-3 bg-tf-bg-muted">
                          <p className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide inline-flex items-center gap-1">
                            <Sparkles className="w-3.5 h-3.5" /> Transition
                          </p>
                          <p className="text-sm text-tf-text mt-2">{safeText(humanizeValue(scene.transitions))}</p>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide inline-flex items-center gap-1">
                          <Tags className="w-3.5 h-3.5" /> Key Elements
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {scene.keyElements.length > 0 ? (
                            scene.keyElements.map((element, index) => (
                              <Badge key={`${element}-${index}`} variant="info">
                                {element}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-tf-text-muted">No key elements extracted.</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </>
  );
}

export default StoryboardGenerator;
